"use client";

import { Users, X } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  ResponsiveAlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Label } from "@/app/_module/components/ui/label";
import { toast } from "@/app/_module/components/ui/use-toast";
import { useCreateHousehold, useAddHouseholdMember } from "../hooks/households";

interface CreateHouseholdMemberModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  leadId: string;
  leadName?: string;
  householdId?: string | null;
  onSuccess?: () => void;
}

export const CreateHouseholdMemberModal = function ({
  isOpen,
  onOpenChange,
  leadId,
  leadName,
  householdId,
  onSuccess,
}: CreateHouseholdMemberModalProps) {
  const [memberName, setMemberName] = useState("");

  const createHouseholdMutation = useCreateHousehold();
  const addMemberMutation = useAddHouseholdMember();

  const handleSubmit = async () => {
    if (!memberName.trim()) {
      toast({
        title: "⚠️ Name Required",
        description: "Please enter a name for the household member",
      });
      return;
    }

    try {
      // If there's a householdId, add member to existing household
      // Otherwise, create a new household with this member
      if (householdId) {
        await addMemberMutation.mutateAsync({
          householdId,
          memberData: { name: memberName.trim() },
        });
      } else {
        await createHouseholdMutation.mutateAsync({
          primaryLeadId: leadId,
          members: [{ name: memberName.trim() }],
        });
      }

      // Reset form and close modal
      setMemberName("");
      onOpenChange(false);
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      toast({
        title: "❌ Failed to Add Member",
        description: error?.message || "An error occurred",
      });
    }
  };

  const handleClose = () => {
    setMemberName("");
    onOpenChange(false);
  };

  const isSubmitting = createHouseholdMutation.isPending || addMemberMutation.isPending;

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <ResponsiveAlertDialogContent size="md">
        <AlertDialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-blue-600" />
              <AlertDialogTitle className="text-lg font-semibold">
                Add Household Member
              </AlertDialogTitle>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 hover:bg-gray-100"
              onClick={handleClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="primary-lead" className="text-sm font-medium">
              Primary Lead
            </Label>
            <Input
              id="primary-lead"
              value={leadName || leadId}
              disabled
              className="bg-gray-50"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="member-name" className="text-sm font-medium">
              Household Member Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="member-name"
              placeholder="Enter member name"
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isSubmitting) {
                  handleSubmit();
                }
              }}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !memberName.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? "Adding..." : "Add Member"}
            </Button>
          </div>
        </div>
      </ResponsiveAlertDialogContent>
    </AlertDialog>
  );
};

export default CreateHouseholdMemberModal;